/**
 * PostDetailHeader 컴포넌트
 * @description 표제 블록 — (눈썹: 주제) · 표제 · 리드 · (바이라인). 눈썹과 바이라인은 폰·태블릿
 *   에서만 선다. 데스크톱은 같은 정보를 왼쪽 곁글(`PostDetailMeta`)이 맡는다.
 *
 *   제목이 없는 글(본문이 제목 자리에 서는 글)은 본문 첫 줄을 표제로 세운다 — 카드가 그렇게
 *   보여줬으니 상세도 같아야 한다.
 */
import { memo } from "react";

import PostAuthorAvatar from "./PostAuthorAvatar";
import { postDetailStyles } from "./PostDetail.styles";
import type { PostDetailHeaderProps } from "./PostDetail.types";

import { blogCopyOf } from "../../constants/blogCopy";
import { formatPostDate } from "../../utils/formatPostDate";
import { Reveal, REVEAL_STAGGER, RevealText } from "@/shared/ui";

/** 제목 없는 글의 제목 — 본문 첫 문단을 한 줄로 */
const TITLE_MAX = 80;
const titleOf = (post: PostDetailHeaderProps["post"]): string =>
  post.title.trim() ||
  post.content
    .replace(/^#+\s*/gm, "")
    .replace(/\s+/g, " ")
    .trim()
    .slice(0, TITLE_MAX);

const PostDetailHeader = memo<PostDetailHeaderProps>(
  ({ post, lede, readingMinutes }) => {
    const topic = post.category?.name;
    const styles = postDetailStyles;
    const copy = blogCopyOf(post);

    return (
      <header className={styles.header}>
        {topic ? <p className={styles.eyebrow}>{topic}</p> : null}
        {/* 표제는 낱말이 차례로 떠오르고, 리드와 바이라인이 그 뒤를 따른다 */}
        <RevealText as="h1" text={titleOf(post)} className={styles.title} />
        {lede ? (
          <Reveal delay={REVEAL_STAGGER * 4}>
            <p className={styles.lede}>{lede}</p>
          </Reveal>
        ) : null}

        {/* 바이라인 — 리드 아래 한 줄. 얼굴 · 이름 / 날짜 · 읽는 시간 */}
        <Reveal delay={REVEAL_STAGGER * 6} className={styles.byline.root}>
          <PostAuthorAvatar author={post.author} />
          <div className={styles.byline.texts}>
            <span className={styles.byline.name}>{post.author.name}</span>
            <span className={styles.byline.meta}>
              <time dateTime={post.publishedAt}>
                {formatPostDate(post.publishedAt)}
              </time>
              {" · "}
              {copy.minRead(readingMinutes)}
            </span>
          </div>
        </Reveal>
      </header>
    );
  },
);

PostDetailHeader.displayName = "PostDetailHeader";

export default PostDetailHeader;
